import { z } from 'zod'

export const serviceNameSchema = z.enum([
  'apiDesign',
  'apiDevelopment',
  'apiMock',
  'apiTest',
  'apigee',
  'kong',
  'cicdAutomation',
  'codeReview',
  'compliance',
  'consumer',
  'contractTesting',
  'onboarding',
  'provider',
  'platformOnboarding',
  'requirements',
  'specGeneration',
  'testGeneration',
  'webIde',
  'workflowOrchestrator',
])

export type ServiceName =
  z.infer<typeof serviceNameSchema> | 'mcpGeneration' | 'proby'

const serviceUrlsSchema = z
  .object(
    Object.fromEntries(
      serviceNameSchema.options.map((name) => [name, z.string().min(1)]),
    ) as Record<z.infer<typeof serviceNameSchema>, z.ZodString>,
  )
  .extend({
    mcpGeneration: z.string().min(1).optional(),
    proby: z.string().min(1).optional(),
    workflowOrchestrator: z.string().min(1).optional(),
  })

const runtimeConfigSchema = z.object({
  environment: z.string().min(1),
  release: z.string().min(1),
  services: serviceUrlsSchema,
  links: z.object({
    onboardingConsole: z.url(),
    lifecycleConsole: z.url().optional(),
  }),
})

export type RuntimeConfig = z.infer<typeof runtimeConfigSchema>

let activeConfig: RuntimeConfig | null = null
let activeConfigRequest: Promise<RuntimeConfig> | null = null

export function mergeRuntimeConfig(
  fileConfig: RuntimeConfig,
  serviceOverrides: Partial<Record<ServiceName, string>>,
  onboardingConsole?: string,
): RuntimeConfig {
  return runtimeConfigSchema.parse({
    ...fileConfig,
    services: {
      ...fileConfig.services,
      ...serviceOverrides,
    },
    links: {
      ...fileConfig.links,
      ...(onboardingConsole ? { onboardingConsole } : {}),
    },
  })
}

function readDevelopmentServiceOverrides(): Partial<
  Record<ServiceName, string>
> {
  if (!import.meta.env.DEV) return {}

  const services: Partial<Record<ServiceName, string | undefined>> = {
    mcpGeneration: import.meta.env.VITE_MCP_GENERATION_BASE_URL,
    proby: import.meta.env.VITE_PROBY_BASE_URL,
    apiDesign: import.meta.env.VITE_API_DESIGN_BASE_URL,
    apiDevelopment: import.meta.env.VITE_API_DEVELOPMENT_BASE_URL,
    apiMock: import.meta.env.VITE_API_MOCK_BASE_URL,
    apiTest: import.meta.env.VITE_API_TEST_BASE_URL,
    apigee: import.meta.env.VITE_APIGEE_BASE_URL,
    kong: import.meta.env.VITE_KONG_BASE_URL,
    cicdAutomation: import.meta.env.VITE_CICD_AUTOMATION_BASE_URL,
    codeReview: import.meta.env.VITE_CODE_REVIEW_BASE_URL,
    compliance: import.meta.env.VITE_COMPLIANCE_BASE_URL,
    consumer: import.meta.env.VITE_CONSUMER_BASE_URL,
    contractTesting: import.meta.env.VITE_CONTRACT_TESTING_BASE_URL,
    onboarding: import.meta.env.VITE_ONBOARDING_BASE_URL,
    platformOnboarding: import.meta.env.VITE_PLATFORM_ONBOARDING_BASE_URL,
    provider: import.meta.env.VITE_PROVIDER_BASE_URL,
    requirements: import.meta.env.VITE_REQUIREMENTS_BASE_URL,
    specGeneration: import.meta.env.VITE_SPEC_GENERATION_BASE_URL,
    testGeneration: import.meta.env.VITE_TEST_GENERATION_BASE_URL,
    webIde: import.meta.env.VITE_WEB_IDE_BASE_URL,
    workflowOrchestrator: import.meta.env.VITE_WORKFLOW_ORCHESTRATOR_BASE_URL,
  }

  return Object.fromEntries(
    Object.entries(services).filter((entry): entry is [ServiceName, string] =>
      Boolean(entry[1]?.trim()),
    ),
  )
}

export async function loadRuntimeConfig(): Promise<RuntimeConfig> {
  if (activeConfig) return activeConfig
  if (activeConfigRequest) return activeConfigRequest

  activeConfigRequest = (async () => {
    const configUrl = import.meta.env.VITE_RUNTIME_CONFIG_URL || '/config.json'
    const response = await fetch(configUrl, {
      cache: 'no-store',
      credentials: 'same-origin',
    })

    if (!response.ok) {
      throw new Error(
        `Unable to load runtime configuration (${response.status})`,
      )
    }

    const fileConfig = runtimeConfigSchema.parse(await response.json())
    const onboardingConsole = import.meta.env.DEV
      ? import.meta.env.VITE_ONBOARDING_CONSOLE_URL
      : undefined

    activeConfig = mergeRuntimeConfig(
      fileConfig,
      readDevelopmentServiceOverrides(),
      onboardingConsole,
    )
    return activeConfig
  })().catch((error: unknown) => {
    activeConfigRequest = null
    throw error
  })

  return activeConfigRequest
}

export function getRuntimeConfig(): RuntimeConfig {
  if (!activeConfig) {
    throw new Error('Runtime configuration has not been initialized')
  }
  return activeConfig
}
